import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FabricCard } from "@/components/FabricCard";
import { Checkbox } from "@/components/ui/checkbox";
import { FabricForm } from "@/components/FabricForm";
import { ExportDialog } from "@/components/ExportDialog";
import { api, formatApiErrorDetail } from "@/api";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { Download, Folder, LogOut, Search, AlertTriangle, CheckSquare, LayoutGrid, List, ArrowUp, ArrowDown, ChevronsUpDown, Share2, QrCode } from "lucide-react";
import { FabricRow } from "@/components/FabricRow";
import { ImageLightbox } from "@/components/ImageLightbox";
import { ShareCatalogueDialog } from "@/components/ShareCatalogueDialog";
import { AppShell } from "@/components/AppShell";
import { STAT_TONES } from "@/lib/moduleTheme";
import { FabricImage } from "@/components/FabricImage";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [colour, setColour] = useState("all");
  const [centre, setCentre] = useState("all");
  const [selected, setSelected] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("inventory_view") || "grid");
  const [sort, setSort] = useState({ key: "shade_number", dir: "asc" });
  const [previewIdx, setPreviewIdx] = useState(null);

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const setViewMode = (v) => {
    setView(v);
    localStorage.setItem("inventory_view", v);
  };

  const printLabels = async () => {
    try {
      const res = await api.post("/qr-labels", { ids: selected }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "qr-labels.pdf";
      a.click();
      toast.success("QR label sheet downloaded");
    } catch {
      toast.error("Could not build the QR labels");
    }
  };


  const load = async () => {
    try {
      const { data } = await api.get("/fabrics");
      // hide the product placeholder bucket (shade-wise product with no shade yet)
      setItems(data.filter((f) => !((f.product_type || "shade_wise") === "shade_wise" && !f.shade_number)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadProfile();
  }, []);

  const loadProfile = () => api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});

  const colours = useMemo(
    () => [...new Set(items.map((i) => i.quality_name).filter(Boolean))].sort(),
    [items]
  );

  const centres = useMemo(
    () => [...new Set(items.map((i) => i.centre).filter(Boolean))].sort(),
    [items]
  );

  const filtered = useMemo(
    () =>
      items.filter((i) => {
        const text = `${i.quality_name} ${i.shade_number || ""} ${i.colour || ""} ${i.company_name || ""} ${i.centre || ""}`.toLowerCase();
        return (
          text.includes(q.toLowerCase()) &&
          (colour === "all" || i.quality_name === colour) &&
          (centre === "all" || i.centre === centre)
        );
      }),
    [items, q, colour, centre]
  );

  const grouped = useMemo(() => {
    const num = (v) => Number(v) || 0;
    const value = (i) => num(i.meter_stock) * num(i.purchase_price);
    const cmp = (a, b) => {
      const { key, dir } = sort;
      const sign = dir === "asc" ? 1 : -1;
      let r;
      if (key === "than_stock" || key === "meter_stock" || key === "wsr") {
        r = num(a[key]) - num(b[key]);
      } else if (key === "value") {
        r = value(a) - value(b);
      } else {
        r = String(a[key] ?? "").localeCompare(String(b[key] ?? ""), undefined, { numeric: true });
      }
      return r * sign;
    };
    const map = {};
    filtered.forEach((i) => {
      const key = i.quality_name || "Unnamed quality";
      (map[key] = map[key] || []).push(i);
    });
    return Object.entries(map)
      .map(([k, v]) => [k, [...v].sort(cmp)])
      .sort(([a], [b]) => a.localeCompare(b));
  }, [filtered, sort]);

  const round2 = (v) => Math.round(v * 100) / 100;

  const totals = useMemo(    () => ({
      items: items.length,
      qualities: colours.length,
      than: round2(items.reduce((s, i) => s + (Number(i.than_stock) || 0), 0)),
      meter: round2(items.reduce((s, i) => s + (Number(i.meter_stock) || 0), 0)),
      cost_value: items.reduce((s, i) => s + (Number(i.meter_stock) || 0) * (Number(i.purchase_price) || 0), 0),
      sales_value: items.reduce((s, i) => s + (Number(i.meter_stock) || 0) * (Number(i.wsr) || 0), 0),
    }),
    [items, colours]
  );

  const negativeStock = useMemo(
    () => items.filter((i) => Number(i.meter_stock) < 0 || Number(i.than_stock) < 0),
    [items]
  );

  const lowStock = useMemo(
    () => items.filter((i) => Number(i.min_stock) > 0 && Number(i.than_stock) < Number(i.min_stock)),
    [items]
  );

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const allFilteredIds = useMemo(() => filtered.map((i) => i.id), [filtered]);

  // flat list in the order shown on screen, so the lightbox can step through shades
  const previewList = useMemo(() => grouped.flatMap(([, list]) => list), [grouped]);

  const openPreview = (item) => {
    const idx = previewList.findIndex((i) => i.id === item.id);
    if (idx >= 0) setPreviewIdx(idx);
  };
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.includes(id));

  const toggleAll = () => setSelected(allSelected ? [] : allFilteredIds);

  const toggleGroup = (list) => {
    const ids = list.map((i) => i.id);
    const every = ids.every((id) => selected.includes(id));
    setSelected((s) => (every ? s.filter((x) => !ids.includes(x)) : [...new Set([...s, ...ids])]));
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/fabrics/${deleteItem.id}`);
      toast.success("Fabric removed");
      setSelected((s) => s.filter((x) => x !== deleteItem.id));
      setDeleteItem(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const Stat = ({ label, value, testId, tone = 0 }) => {
    const c = STAT_TONES[tone % STAT_TONES.length];
    return (
      <div
        data-testid={testId}
        className="min-w-0 rounded-sm border px-3 py-2"
        style={{ background: c.bg, borderColor: `${c.bar}40`, borderLeft: `3px solid ${c.bar}` }}
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: c.label }}>{label}</p>
        <p className="font-mono-num truncate text-lg font-semibold sm:text-xl" style={{ color: c.value }}>{value}</p>
      </div>
    );
  };

  const SortTh = ({ k, children, align = "left", className = "" }) => (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"} ${className}`}>
      <button
        data-testid={`sort-${k}`}
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.15em] transition-colors hover:text-zinc-950 ${sort.key === k ? "text-zinc-950" : ""}`}
      >
        {children}
        {sort.key === k ? (
          sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />
        ) : (
          <ChevronsUpDown size={11} className="opacity-40" />
        )}
      </button>
    </th>
  );


  return (
    <AppShell profile={profile}>
      <div className="pb-28">

      {negativeStock.length > 0 && (
        <div data-testid="negative-stock-alert" className="border-b border-red-300 bg-red-50">
          <div className="mx-auto max-w-[1500px] px-4 py-3 sm:px-8">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
              <div>
                <p className="text-sm font-bold text-red-900">{negativeStock.length} shade(s) in negative stock</p>
                <p className="mt-0.5 font-mono-num text-xs text-red-800">
                  {negativeStock
                    .slice(0, 4)
                    .map((i) => `${i.quality_name}${i.shade_number ? ` · ${i.shade_number}` : ""} (${i.meter_stock} mtr / ${i.than_stock} than)`)
                    .join("  |  ")}
                  {negativeStock.length > 4 ? `  |  +${negativeStock.length - 4} more` : ""}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {lowStock.length > 0 && (
        <div
          data-testid="low-stock-alert"
          className="border-b border-amber-300 bg-amber-50"
        >
          <div className="mx-auto flex max-w-[1500px] flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-bold text-amber-900">
                  {lowStock.length} item(s) below minimum stock
                </p>
                <p className="mt-0.5 text-xs text-amber-800">
                  {lowStock
                    .slice(0, 4)
                    .map((i) => `${i.quality_name} · shade ${i.shade_number} (${i.than_stock}/${i.min_stock} than)`)
                    .join("  |  ")}
                  {lowStock.length > 4 ? `  |  +${lowStock.length - 4} more` : ""}
                </p>
              </div>
            </div>
            <Button
              data-testid="low-stock-select-button"
              size="sm"
              variant="outline"
              className="shrink-0 rounded-sm border-amber-500 bg-white text-amber-900 hover:bg-amber-100"
              onClick={() => setSelected(lowStock.map((i) => i.id))}
            >
              Select these
            </Button>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band flex flex-col gap-6 rounded-sm px-5 py-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#0F766E" }}>Inventory</p>
            <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">
              Fabric Stock
            </h1>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:flex-wrap sm:items-end sm:gap-0" data-testid="stats-row">
            <Stat tone={0} label="Items" value={totals.items} />
            <Stat tone={1} label="Qualities" value={totals.qualities} />
            <Stat tone={2} label="Than" value={totals.than} />
            <Stat tone={3} label="Meter" value={totals.meter} />
            <Stat tone={4} testId="stat-stock-value" label="Stock value (cost)" value={`₹${Math.round(totals.cost_value).toLocaleString("en-IN")}`} />
            <Stat tone={5} testId="stat-stock-sales-value" label="Sales value" value={`₹${Math.round(totals.sales_value).toLocaleString("en-IN")}`} />
          </div>
        </div>

        <div className="flex flex-col gap-3 py-6 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search quality, shade no., colour or company"
              className="rounded-sm pl-9"
            />
          </div>
          <Select value={centre} onValueChange={setCentre}>
            <SelectTrigger data-testid="centre-filter" className="w-full rounded-sm sm:w-52">
              <SelectValue placeholder="All centres" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All material centres</SelectItem>
              {centres.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={colour} onValueChange={setColour}>
            <SelectTrigger data-testid="colour-filter" className="w-full rounded-sm sm:w-52">
              <SelectValue placeholder="All qualities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All qualities</SelectItem>
              {colours.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2 sm:contents">
          <div className="col-span-2 flex border border-zinc-300 sm:col-span-1 sm:shrink-0" data-testid="view-toggle">
            <button
              data-testid="view-grid-button"
              onClick={() => setViewMode("grid")}
              title="Grid view"
              className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors sm:flex-none ${view === "grid" ? "bg-[var(--mod-primary)] text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"}`}
            >
              <LayoutGrid size={14} /> Grid
            </button>
            <button
              data-testid="view-list-button"
              onClick={() => setViewMode("list")}
              title="List view"
              className={`flex flex-1 items-center justify-center gap-1.5 border-l border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors sm:flex-none ${view === "list" ? "bg-[var(--mod-primary)] text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"}`}
            >
              <List size={14} /> List
            </button>
          </div>
          <Button
            data-testid="select-all-button"
            variant="outline"
            className="rounded-sm"
            onClick={toggleAll}
          >
            <CheckSquare size={15} className="mr-2" /> {allSelected ? "Unselect all" : "Select all"}
          </Button>
          <Button
            data-testid="export-all-button"
            variant="outline"
            className="rounded-sm border-zinc-950"
            onClick={() => {
              setSelected([]);
              setExportOpen(true);
            }}
          >
            <Download size={15} className="mr-2" /> Export
          </Button>
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading stock…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <img
              src="https://images.unsplash.com/photo-1705250466297-90035b3a2b26?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
              alt="Folded fabric stack"
              className="mx-auto mb-6 h-40 w-full max-w-sm object-cover"
            />
            <h3 className="font-head text-xl font-bold tracking-tight">
              {items.length === 0 ? "No fabric added yet" : "No matches found"}
            </h3>
            <p className="mt-1 text-sm text-zinc-500">
              {items.length === 0
                ? "Add items from Admin → Item Master. They appear here, and stock comes from Purchase or Stock Adjustment."
                : "Try a different search or quality filter."}
            </p>
          </div>
        ) : (
          <div className="space-y-10">
            {grouped.map(([group, list]) => (
              <section key={group} data-testid={`quality-group-${group}`}>
                <div className="mb-4 flex items-center gap-3 border-b border-zinc-200 pb-2">
                  <Checkbox
                    data-testid={`select-group-${group}`}
                    checked={list.every((i) => selected.includes(i.id))}
                    onCheckedChange={() => toggleGroup(list)}
                    className="h-4 w-4 rounded-sm border-zinc-400 data-[state=checked]:bg-zinc-950"
                  />
                  <Folder size={16} className="shrink-0 text-[#C89B3C]" />
                  <h2 className="font-head text-xl font-bold tracking-tight">{group}</h2>
                  <span className="font-mono-num text-xs text-zinc-500">{list.length} shade(s)</span>
                </div>
                <div className={view === "list" ? "hidden" : "grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-6"}>
                  {view === "grid" && list.map((item, idx) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: Math.min(idx * 0.03, 0.3) }}
                    >
                      <FabricCard
                        item={item}
                        selected={selected.includes(item.id)}
                        onToggle={toggle}
                        onEdit={(it) => {
                          setEditItem(it);
                          setFormOpen(true);
                        }}
                        onDelete={setDeleteItem}
                        onPreview={openPreview}
                      />
                    </motion.div>
                  ))}
                </div>
                {view === "list" && (
                  <div className="overflow-x-auto border border-zinc-200">
                    <table className="w-full min-w-[560px] sm:min-w-[720px] border-collapse" data-testid={`fabric-table-${group}`}>
                      <thead>
                        <tr className="border-b border-zinc-300 bg-zinc-50 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                          <th className="px-3 py-2 w-8"></th>
                          <th className="hidden px-2 py-2 w-12 sm:table-cell">Img</th>
                          <SortTh k="shade_number">Shade</SortTh>
                          <SortTh k="quality_name">Quality</SortTh>
                          <SortTh k="colour" className="hidden md:table-cell">Colour</SortTh>
                          <SortTh k="company_name" className="hidden lg:table-cell">Company</SortTh>
                          <SortTh k="centre" className="hidden sm:table-cell">Centre</SortTh>
                          <SortTh k="than_stock" align="right">Than</SortTh>
                          <SortTh k="meter_stock" align="right">Meter</SortTh>
                          <SortTh k="wsr" align="right" className="hidden lg:table-cell">WSR</SortTh>
                          <SortTh k="value" align="right" className="hidden xl:table-cell">Value</SortTh>
                          <th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((item) => (
                          <FabricRow
                            key={item.id}
                            item={item}
                            selected={selected.includes(item.id)}
                            onToggle={toggle}
                            onEdit={(it) => {
                              setEditItem(it);
                              setFormOpen(true);
                            }}
                            onDelete={setDeleteItem}
                            onPreview={openPreview}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </main>

      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div
            initial={{ y: 120 }}
            animate={{ y: 0 }}
            exit={{ y: 120 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            data-testid="bulk-action-bar"
            className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-md border border-zinc-950 bg-white p-3 shadow-[4px_4px_0px_0px_rgba(9,9,11,1)]"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono-num text-sm font-semibold" data-testid="selected-count">
                {selected.length} selected
              </span>
              <div className="flex flex-1 justify-end gap-2">
                <Button
                  data-testid="clear-selection-button"
                  variant="ghost"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => setSelected([])}
                >
                  Clear
                </Button>
                <Button
                  data-testid="share-catalogue-button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm border-zinc-950"
                  onClick={() => setShareOpen(true)}
                >
                  <Share2 size={14} className="mr-1.5" /> Share
                </Button>
                <Button
                  data-testid="qr-labels-button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm border-zinc-950"
                  onClick={printLabels}
                >
                  <QrCode size={14} className="mr-1.5" /> QR
                </Button>
                <Button
                  data-testid="export-selected-button"
                  size="sm"
                  className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
                  onClick={() => setExportOpen(true)}
                >
                  <Download size={14} className="mr-1.5" /> Export
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FabricForm open={formOpen} onOpenChange={setFormOpen} editItem={editItem} onSaved={load} />
      <ImageLightbox
        items={previewList}
        index={previewIdx}
        onIndexChange={setPreviewIdx}
        onClose={() => setPreviewIdx(null)}
      />
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} ids={selected} />
      <ShareCatalogueDialog open={shareOpen} onOpenChange={setShareOpen} ids={selected} />

      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head tracking-tight">Remove this fabric?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItem?.quality_name} (shade {deleteItem?.shade_number}) will be removed from your stock.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AppShell>
  );
}
